/** Public, provider-independent measurement contract. Never put credentials here. */
export const ANALYTICS_SCHEMA_VERSION = "1";
export const ANALYTICS_CONTEXT_KEYS = ["site_id", "page_id", "surface", "content_group", "site_language", "schema_version"] as const;
export const ANALYTICS_EVENTS = ["page_view", "scroll", "click", "file_download", "search", "card_click", "contact_click", "job_view", "job_apply_click", "company_click", "article_view", "article_click", "event_view", "sign_up", "generate_lead", "booking_complete", "purchase"] as const;
export const ANALYTICS_KEY_EVENTS = ["sign_up", "generate_lead", "booking_complete", "purchase"] as const;
export type AnalyticsContext = { site_id: string; page_id: string; surface: string; content_group: string; site_language: string; schema_version: string };
export type ReportRange = { from: string; to: string; days: number };
export type ReportStatus = "ok" | "empty" | "unconfigured" | "error" | "rate_limited";
export type SourceReport = {
  source: string; status: ReportStatus; range: ReportRange; timezone: string;
  checkedAt: string; dataThrough: string | null; totals: Record<string, number>;
  rows: Array<Record<string, string | number>>; error?: string; truncated?: boolean;
};

export function isAnalyticsPath(path: string): boolean {
  return !/^\/(?:admin|platform-admin|api|auth|oauth|account|app|login|lesson|material|claim)(?:[/.]|$)/i.test(path)
    && !/\.(?:js|css|json|xml|png|jpe?g|webp|svg|woff2?)$/i.test(path);
}

export function safeCampaignValue(value: unknown): string {
  const text = String(value || "").trim().toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{0,79}$/.test(text) ? text : "";
}

export function safeAnalyticsUrl(value: string, base = "https://myreach.top"): string {
  try {
    const url = new URL(value, base);
    if (!/^https?:$/.test(url.protocol) || url.username || url.password || !isAnalyticsPath(url.pathname)) return "";
    if (/@|%40|%2540/i.test(url.pathname)) return "";
    const query = new URLSearchParams();
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
      const clean = safeCampaignValue(url.searchParams.get(key));
      if (clean) query.set(key, clean);
    }
    return `${url.origin}${url.pathname}${query.size ? `?${query}` : ""}`;
  } catch { return ""; }
}

/** Only business metadata; deliberately excludes raw search text and generic source/medium. */
export function safeEventParameters(raw: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  const names = new Set(["job_id", "company_slug", "slug", "card_id", "card_type", "interaction_source", "link_domain", "link_type", "method", "outcome", "currency", "transaction_id", "percent_scrolled", "value", "result_count", "filter_count", "query_length", "has_query", "remote_only", "top_internships"]);
  for (const [name, value] of Object.entries(raw)) {
    if (!names.has(name)) continue;
    if (typeof value === "boolean") out[name] = value;
    else if (typeof value === "number" && Number.isFinite(value)) out[name] = value;
    else if (typeof value === "string" && /^[a-z0-9][a-z0-9_.:/-]{0,119}$/i.test(value)) out[name] = value;
  }
  return out;
}

export function contentGroup(path: string, creator = false): string {
  if (creator) return "client_page";
  if (/^\/(?:learn-ai|blog|article)(?:\/|$)/.test(path)) return "articles";
  if (/^\/(?:events|event)(?:\/|$)/.test(path)) return "events";
  if (/^\/(?:companies|company)(?:\/|$)/.test(path)) return "companies";
  if (/^\/(?:jobs?|ai-jobs|ai-jobs-in-)/.test(path) || path === "/") return "landing";
  return path.split("/").filter(Boolean)[0] || "landing";
}

export function analyticsContext(siteId: string, path: string, pageId = "", language = "en", creator = false): AnalyticsContext {
  return { site_id: siteId, page_id: pageId || `${siteId}:platform`, surface: creator ? "client_page" : "public_website", content_group: contentGroup(path, creator), site_language: /^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/i.test(language) ? language.toLowerCase() : "en", schema_version: ANALYTICS_SCHEMA_VERSION };
}

function calendarDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) throw new Error("Invalid report date");
  return value;
}

export function reportRange(input: number | { from?: string; to?: string; days?: number } = 30, now = new Date()): ReportRange {
  const requested = typeof input === "number" ? { days: input } : input;
  const to = calendarDate((requested.to || now.toISOString()).slice(0, 10));
  const days = Math.max(1, Math.min(400, Math.floor(Number(requested.days) || 30)));
  const from = calendarDate((requested.from || new Date(Date.parse(`${to}T00:00:00Z`) - (days - 1) * 86_400_000).toISOString()).slice(0, 10));
  const count = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
  if (count < 1 || count > 400) throw new Error("Report range must contain 1–400 days");
  return { from, to, days: count };
}

export function previousRange(range: ReportRange): ReportRange {
  const end = new Date(Date.parse(`${range.from}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  return reportRange({ to: end, days: range.days });
}

export function parisSchedule(now = new Date()): { day: string; dailyDue: boolean; weeklyDue: boolean; week: string } {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", hourCycle: "h23", weekday: "short" }).formatToParts(now);
  const get = (key: string): string => parts.find((part) => part.type === key)?.value || "";
  const day = `${get("year")}-${get("month")}-${get("day")}`;
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  const monday = new Date(Date.parse(`${day}T00:00:00Z`) - ((weekday + 6) % 7) * 86_400_000).toISOString().slice(0, 10);
  const hour = Number(get("hour"));
  return { day, dailyDue: hour >= 6, weeklyDue: get("weekday") === "Mon" ? hour >= 9 : weekday > 1 || weekday === 0, week: monday };
}

export function campaignUrl(canonicalUrl: string, source: string, medium: string, campaign: string, content = ""): string {
  const url = new URL(canonicalUrl);
  if (url.protocol !== "https:" || url.username || url.password) throw new Error("Canonical HTTPS URL required");
  for (const [key, value] of [["utm_source", source], ["utm_medium", medium], ["utm_campaign", campaign], ["utm_content", content]]) {
    const clean = safeCampaignValue(value);
    if (!clean && (key !== "utm_content" || value)) throw new Error(`Invalid ${key}`);
    if (clean) url.searchParams.set(key, clean);
  }
  return url.href;
}
